import { initContract } from "@ts-rest/core";
import { z } from "zod";

import { ApiErrorSchema, UnitSummarySchema } from "./common";

const c = initContract();

export const NotificationCategoryNameSchema = z.enum([
  "UrgentOperationalRequests",
  "NonUrgentOperationalRequests",
  "Activities",
  "Messages",
  "UrgentOutOfArea",
  "NonUrgentOutOfArea",
  "OutOfAreaApprovals",
  "Other",
]);

export const NotificationToneSchema = z.enum([
  "default",
  "alarm_long",
  "alarm",
  "alert",
  "announcement_long",
  "announcement",
  "bells",
  "bright",
  "digital_long",
  "digital",
  "flute",
  "gong",
  "horn",
  "pager_long",
  "pager",
  "rising",
  "wail_long",
  "wail",
  "whistle",
  "yelp_long",
  "yelp",
  "none",
]);

const NotificationSettingsCategoryInputSchema = z.object({
  categoryName: NotificationCategoryNameSchema,
  tone: NotificationToneSchema,
  volume: z.number(),
  critical: z.boolean(),
});

const NotificationSettingsCategorySchema =
  NotificationSettingsCategoryInputSchema.extend({
    id: z.string(),
  });

const NotificationSettingsInputSchema = z.object({
  nonUrgentRequestNotificationsMuted: z.boolean().optional(),
  categories: z.array(NotificationSettingsCategoryInputSchema).optional(),
});

const NotificationSettingsSchema = z.object({
  nonUrgentRequestNotificationsMuted: z.boolean(),
  nonUrgentRequestNotificationsMutedUntil: z.string().datetime().optional(),
  categories: z.array(NotificationSettingsCategorySchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const OOAAApprovalNotificationSettingsUnitSchema = z.object({
  unit: UnitSummarySchema,
  notify: z.boolean(),
});

const OOAAApprovalNotificationSettingsSchema = z.object({
  units: z.array(OOAAApprovalNotificationSettingsUnitSchema),
});

export const notificationSettingsContract = c.router({
  get: {
    method: "GET",
    path: "/notification-settings",
    responses: {
      200: NotificationSettingsSchema,
      404: ApiErrorSchema,
    },
    summary: "Get the notification settings for the current member",
  },
  update: {
    method: "PATCH",
    path: "/notification-settings",
    body: NotificationSettingsInputSchema,
    responses: {
      200: NotificationSettingsSchema,
      400: ApiErrorSchema,
    },
    summary: "Update notification settings",
  },
  getOOAAApprovalSettings: {
    method: "GET",
    path: "/notification-settings/out-of-area-approvals",
    responses: {
      200: OOAAApprovalNotificationSettingsSchema,
      400: ApiErrorSchema,
    },
    summary: "Get out of area approval notification settings",
  },
  updateOOAAApprovalSettings: {
    method: "PATCH",
    path: "/notification-settings/out-of-area-approvals",
    body: z.object({
      unit: z.object({
        unitId: z.string(),
        notify: z.boolean(),
      }),
    }),
    responses: {
      200: OOAAApprovalNotificationSettingsSchema,
      400: ApiErrorSchema,
    },
    summary: "Update out of area approval notification settings",
  },
  sendTestNotification: {
    method: "POST",
    path: "/notification-settings/send-test",
    query: z.object({
      categoryName: NotificationCategoryNameSchema,
    }),
    body: null,
    responses: {
      200: null,
      404: ApiErrorSchema,
    },
    summary: "Send a test notification to the member for the given category",
  },
});

export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

export type NotificationSettingsCategory = z.infer<
  typeof NotificationSettingsCategorySchema
>;

export type NotificationCategoryName = z.infer<
  typeof NotificationCategoryNameSchema
>;

export type NotificationTone = z.infer<typeof NotificationToneSchema>;

export type OOAAApprovalNotificationSettings = z.infer<
  typeof OOAAApprovalNotificationSettingsSchema
>;
